import { useCallback, useMemo, useState, type ReactNode } from 'react';

import type { CompanyResponse } from '../../api';
import {
  AsyncSection,
  Pagination,
  Panel,
  usePagination,
  type ColumnFiltersState,
  type SortingState,
} from '../../components';
import { formatDate, todayInAppTimeZone } from '../../format';
import { staffCompanyPersonPath } from '../../routes/paths';
import { useCompanyContracts, useCompanyRoster, useCompanyScheduledDeltas } from '../queries';
import { SeguradosTable, type SeguradoRow } from './SeguradosTable';
import { SEGURADOS_COPY, coveredCount, entryLabel } from './segurados';
import {
  SEGURADOS_PAGE_SIZE,
  contractsFilterOptions,
  entriesInView,
  filterSegurados,
  isNarrowed,
  scheduledInView,
  seguradosFilterFrom,
  seguradosFootnote,
  sortSegurados,
} from './seguradosFilters';

/**
 * Who is insured today under any of this company's contracts, for staff
 * answering "is this person registered?".
 *
 * Read-only by design: nothing here writes. A name opens the staff person
 * page (#418), addressed by the opaque id of one of the person's rows -- the
 * page finds the others -- and never by the CPF.
 *
 * It reads the company's population (#416), one item per person with an entry
 * per contract that covers them, so somebody under vida and saúde is one row
 * with two tags. The table's filter row -- name, CPF, a contract or a kind,
 * who has (or has not) a change scheduled -- its sortable headers and the
 * paging all work on what has already arrived (`seguradosFilters`): no request
 * is made per choice, per keystroke, per sort or per page, and none of it is in
 * the address, which on a field carrying a CPF also means no partial CPF ever
 * reaches a URL.
 *
 * A person with a change asked for and not yet happened is marked as the
 * portal marks them (`areas/shared/roster.ts`), from the pending and
 * approved-future changes of each contract. If those reads fail the table
 * still renders, and says it cannot vouch for the markers.
 */
export function SeguradosPanel({
  company,
  importAction,
}: {
  company: CompanyResponse;
  /**
   * The company-wide import, which the page owns because it swaps the whole
   * page for the import screen. In the header whatever state the panel is in:
   * the two empty states point at it.
   */
  importAction?: ReactNode;
}) {
  const [asOf] = useState(todayInAppTimeZone);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const contracts = useCompanyContracts(company.id);
  const roster = useCompanyRoster(company.id, asOf);
  const contractIds = useMemo(
    () => contracts.data?.items.map((contract) => contract.id) ?? [],
    [contracts.data],
  );
  const scheduledReads = useCompanyScheduledDeltas(company.id, contractIds, asOf);

  const contractsById = useMemo(
    () => new Map((contracts.data?.items ?? []).map((contract) => [contract.id, contract])),
    [contracts.data],
  );

  // The changes on the contracts in view, so that the marker column and the
  // scheduled filter both speak of the same contracts the rows do.
  const scheduledIndex = useMemo(
    () =>
      scheduledInView(
        scheduledReads.deltas,
        contractsById,
        seguradosFilterFrom(columnFilters),
        asOf,
      ),
    [scheduledReads.deltas, contractsById, columnFilters, asOf],
  );

  const filter = useMemo(
    () => seguradosFilterFrom(columnFilters, scheduledIndex),
    [columnFilters, scheduledIndex],
  );
  const inView = useCallback((person: SeguradoRow) => entriesInView(person, filter), [filter]);
  const people = useMemo(
    () => sortSegurados(filterSegurados(roster.data?.items ?? [], filter), sorting, inView),
    [roster.data, filter, sorting, inView],
  );
  const pagination = usePagination(people, SEGURADOS_PAGE_SIZE);
  const contractOptions = useMemo(
    () => contractsFilterOptions(contracts.data?.items),
    [contracts.data],
  );

  const position = `Posição em ${formatDate(asOf)}.`;
  const description =
    roster.data === undefined
      ? `${SEGURADOS_COPY.description} ${position}`
      : `${SEGURADOS_COPY.description} ${position} ${coveredCount(roster.data.count)}.`;

  return (
    <Panel
      title={SEGURADOS_COPY.title}
      description={description}
      {...(importAction !== undefined && { actions: importAction })}
    >
      <AsyncSection
        state={contracts}
        loadingLabel={SEGURADOS_COPY.contractsLoading}
        errorTitle={SEGURADOS_COPY.contractsError}
        isEmpty={(data) => data.items.length === 0}
        emptyTitle={SEGURADOS_COPY.noContracts}
        emptyDescription={SEGURADOS_COPY.noContractsDescription}
      >
        {() => (
          <AsyncSection
            state={roster}
            loadingLabel={SEGURADOS_COPY.loading}
            errorTitle={SEGURADOS_COPY.error}
            isEmpty={(data) => data.items.length === 0}
            emptyTitle={SEGURADOS_COPY.empty}
            emptyDescription={SEGURADOS_COPY.emptyDescription}
          >
            {(data) => (
              <>
                <SeguradosTable
                  id="company-segurados"
                  caption={SEGURADOS_COPY.caption}
                  people={pagination.items}
                  entriesInView={inView}
                  // Any change to what is filtered or how it is sorted starts
                  // again from page 1.
                  columnFilters={columnFilters}
                  onColumnFiltersChange={(next) => {
                    setColumnFilters(next);
                    pagination.setPage(1);
                  }}
                  sorting={sorting}
                  onSortingChange={(next) => {
                    setSorting(next);
                    pagination.setPage(1);
                  }}
                  contractOptions={contractOptions}
                  personPath={(entry) =>
                    staffCompanyPersonPath(company.id, entry.insured_person_id)
                  }
                  contractLabel={(entry) => entryLabel(entry, contractsById)}
                  scheduled={scheduledIndex}
                  noMatchesHint={SEGURADOS_COPY.noMatchesHint}
                />
                <Pagination
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onPageChange={pagination.setPage}
                />
                <p className="panel__footnote">
                  {seguradosFootnote({
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    matching: people.length,
                    total: data.count,
                    narrowed: isNarrowed(filter),
                  })}
                </p>
                {scheduledReads.isError && (
                  // Without this the table would understate itself: somebody
                  // with an exclusion already filed would look ordinary, which
                  // is exactly the wrong thing to read out on the phone.
                  <p className="panel__footnote panel__footnote--warning" role="alert">
                    {SEGURADOS_COPY.scheduledUnavailable}
                  </p>
                )}
              </>
            )}
          </AsyncSection>
        )}
      </AsyncSection>
    </Panel>
  );
}
